import { useCallback, useMemo, useRef, useState } from 'react';
import { renderResolvedSystemLandscapeSvg } from '@protoss/doc';
import { buildDealSystemLandscape } from '@protoss/shared';
import { Button } from '@protoss/ui';
import { downloadDiagramPdf, type PdfLegendItem } from '../../bpr/utils/diagramPdf';
import {
  getAnswerValueByFieldKey,
  type QuestionnaireAnswerMap,
  type QuestionnaireFieldLookup,
} from '../../../features/sales/utils/questionnaire-field-visibility';
import { resolveTechStackSystemsFromAnswers } from './techStackLandscapeUtils';

interface CurrentTechnologyStackLandscapeProps {
  answers: QuestionnaireAnswerMap;
  fieldLookup?: QuestionnaireFieldLookup;
  /** Centre node label (the new platform). */
  hubName?: string;
  /**
   * The deal's product key. It picks the picture, exactly as the SOW export does: Maxio deals draw
   * from their Maxio answers (no tech-stack editor); every other product draws the same
   * left-to-right logo diagram from its tech-stack systems, with its own logo in the centre.
   */
  productKey?: string | null;
}

const LEGEND: Array<{ tone: string; label: string }> = [
  { tone: 'success', label: 'Keep' },
  { tone: 'danger', label: 'Sunset' },
  { tone: 'neutral', label: 'TBD' },
];

// Mirrors the on-screen legend in the SVG renderer's light palette (the tone
// colors in packages/doc/src/system-landscape-svg.ts), so the PDF legend
// matches the captured drawing.
const PDF_LEGEND: PdfLegendItem[] = [
  { label: 'Keep', swatch: 'node', color: '#a7dab5', fill: '#eaf7ee' },
  { label: 'Sunset', swatch: 'node', color: '#f4b4b4', fill: '#fdecec' },
  { label: 'TBD', swatch: 'node', color: '#e5e7eb', fill: '#ffffff' },
];

/**
 * Read-only current → future systems landscape. Renders the same SVG the SOW embeds (one
 * renderer, identical output): a 3-column landscape (Front Office / Platform / Downstream) with
 * EVERY current system shown and coloured by plan (Keep=green, Sunset=red, TBD=gray). Each in-scope
 * system draws an arrow to the new platform using its integration direction; sunset/out-of-scope
 * systems are shown without a connection.
 */
export function CurrentTechnologyStackLandscape({
  answers,
  fieldLookup,
  hubName,
  productKey,
}: CurrentTechnologyStackLandscapeProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const svg = useMemo(() => {
    const layout = buildDealSystemLandscape({
      productKey,
      answers: {
        getRawValue: (fieldKey) => getAnswerValueByFieldKey(fieldKey, answers, fieldLookup),
        getOptionLabel: (fieldKey, value) =>
          fieldLookup?.get(fieldKey)?.options?.find((opt) => opt.value === value)?.label,
      },
      systems: () => resolveTechStackSystemsFromAnswers(answers, fieldLookup),
      hubName,
    });
    return renderResolvedSystemLandscapeSvg(layout, { background: 'transparent' });
  }, [answers, fieldLookup, hubName, productKey]);

  const handleDownloadPdf = useCallback(async () => {
    if (!canvasRef.current || isExporting) return;
    setIsExporting(true);
    try {
      await downloadDiagramPdf(canvasRef.current, 'Systems Landscape', null, {
        legend: PDF_LEGEND,
      });
    } catch {
      // Best-effort: a failed capture leaves the view untouched.
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  return (
    <div className="tech-stack-landscape">
      <div className="tech-stack-landscape__head">
        <span className="tech-stack-landscape__title">Systems Landscape</span>
        {svg ? (
          <div className="tech-stack-landscape__head-actions">
            <div className="tech-stack-landscape__legend">
              {LEGEND.map((item) => (
                <span key={item.label} className="tech-stack-landscape__legend-item">
                  <span
                    className={`tech-stack-landscape__legend-dot tech-stack-landscape__legend-dot--${item.tone}`}
                    aria-hidden="true"
                  />
                  {item.label}
                </span>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isExporting}
              onClick={() => void handleDownloadPdf()}
            >
              {isExporting ? 'Exporting…' : 'Download PDF'}
            </Button>
          </div>
        ) : null}
      </div>
      {svg ? (
        <div
          ref={canvasRef}
          className="tech-stack-landscape__canvas"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="tech-stack-landscape__empty">
          The systems landscape appears as you add technologies.
        </div>
      )}
    </div>
  );
}
